import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export function useTenants() {
  const queryClient = useQueryClient();

  const { data: tenants, isLoading, refetch } = useQuery({
    queryKey: ['admin_tenants'],
    queryFn: async () => {
      const { data: tenantsData, error } = await supabase
        .from('tenants')
        .select(`
          *,
          subscription_plans (
            name,
            price_monthly
          )
        `)
        .order('created_at', { ascending: false });
      
      if (error) throw error;

      // Buscar informações do admin e contagem de usuários para cada tenant
      const tenantsWithAdminInfo = await Promise.all(
        (tenantsData || []).map(async (tenant) => {
          const { data: adminInfo, error: adminError } = await supabase.rpc('get_tenant_admin_info', {
            p_tenant_id: tenant.id
          });

          // Debug: Log para verificar retorno
          console.log(`📊 Admin Info para ${tenant.name}:`, {
            adminInfo,
            adminError,
            hasData: !!adminInfo,
            isArray: Array.isArray(adminInfo),
            firstItem: adminInfo?.[0]
          });

          // Contar usuários da tenant
          const { count: userCount } = await supabase
            .from('user_roles')
            .select('*', { count: 'exact', head: true })
            .eq('tenant_id', tenant.id);

          return {
            ...tenant,
            adminInfo: Array.isArray(adminInfo) && adminInfo.length > 0 ? adminInfo[0] : null,
            userCount: userCount || 0
          };
        })
      );
      
      return tenantsWithAdminInfo;
    },
  });

  const createDemoTenant = useMutation({
    mutationFn: async (input: any) => {
      const { data, error } = await supabase.functions.invoke(
        'create-demo-tenant',
        { body: input }
      );
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tenants'] });
    },
    onError: (error: any) => {
      console.error('❌ Erro completo ao criar tenant:', error);
      
      // Extrair mensagem amigável do erro da edge function
      const userFriendlyMessage = error?.userFriendlyMessage || 
                                  error?.error?.userFriendlyMessage ||
                                  error?.message || 
                                  'Erro ao criar tenant demo';
      
      const technicalDetails = error?.details || error?.error?.message || '';
      
      toast.error(userFriendlyMessage, {
        description: technicalDetails || undefined,
      });
      
      console.error('❌ Erro detalhado:', {
        userFriendlyMessage,
        technicalDetails,
        fullError: error,
      });
    },
  });

  const deleteTenant = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('is_demo')
        .eq('id', tenantId)
        .single();
      
      if (!tenant?.is_demo) {
        throw new Error('Apenas tenants demo podem ser deletadas');
      }
      
      const { data, error } = await supabase
        .from('tenants')
        .delete()
        .eq('id', tenantId)
        .select();
      
      console.log('🗑️ Resultado da deleção:', { data, error, tenantId });
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Tenant não foi deletada. Verifique as permissões RLS.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tenants'] });
      toast.success('Tenant deletada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao deletar tenant: ' + error.message);
    },
  });

  const inactivateTenant = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('is_demo, is_active')
        .eq('id', tenantId)
        .single();
      
      if (!tenant?.is_demo) {
        throw new Error('Apenas tenants demo podem ser inativadas');
      }
      
      const { data, error } = await supabase
        .from('tenants')
        .update({ is_active: false })
        .eq('id', tenantId)
        .select();
      
      console.log('⛔ Resultado da inativação:', { data, error, tenantId });
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Tenant não foi inativada. Verifique as permissões RLS.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tenants'] });
      toast.success('Tenant inativada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao inativar tenant: ' + error.message);
    },
  });

  const activateTenant = useMutation({
    mutationFn: async (tenantId: string) => {
      const { data: tenant } = await supabase
        .from('tenants')
        .select('is_demo, is_active')
        .eq('id', tenantId)
        .single();
      
      if (!tenant?.is_demo) {
        throw new Error('Apenas tenants demo podem ser ativadas');
      }
      
      const { data, error } = await supabase
        .from('tenants')
        .update({ is_active: true })
        .eq('id', tenantId)
        .select();
      
      console.log('✅ Resultado da ativação:', { data, error, tenantId });
      
      if (error) throw error;
      
      if (!data || data.length === 0) {
        throw new Error('Tenant não foi ativada. Verifique as permissões RLS.');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin_tenants'] });
      toast.success('Tenant ativada com sucesso!');
    },
    onError: (error: any) => {
      toast.error('Erro ao ativar tenant: ' + error.message);
    },
  });

  return {
    tenants,
    isLoading,
    createDemoTenant,
    deleteTenant,
    inactivateTenant,
    activateTenant,
    refetch
  };
}
